import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api";
import Loader from "../components/Loader";
import Breadcrumbs from "../components/Breadcrumbs";
import "../assets/buttons.css";
import "../assets/panels.css";

const BlockCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get("client_id");

  const [client, setClient] = useState(null);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!clientId) {
      toast.error("Client ID is required");
      navigate("/clients");
      return;
    }

    // Fetch client details
    const fetchClient = async () => {
      try {
        const response = await api.get(`/clients/${clientId}`);
        if (response.data.client) {
          setClient(response.data.client);
        } else {
          toast.error("Client not found");
          navigate("/clients");
        }
      } catch (error) {
        console.error("Error fetching client:", error);
        toast.error("Failed to load client");
        navigate("/clients");
      } finally {
        setLoading(false);
      }
    };

    fetchClient();
  }, [clientId, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    // Validation
    if (!name.trim()) {
      setErrors({ name: "Block name is required" });
      return;
    }

    setSubmitting(true);

    try {
      const response = await api.post("/blocks", {
        client_id: parseInt(clientId),
        name: name.trim(),
      });

      if (response.data.id) {
        toast.success("Block created successfully!");
        navigate(`/clients/${clientId}`);
      } else {
        toast.error("Failed to create block");
      }
    } catch (error) {
      console.error("Error creating block:", error);
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        toast.error(error.response?.data?.message || "Failed to create block");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  const breadcrumbs = [
    { label: "Dashboard", link: "/dashboard" },
    { label: "Clients", link: "/clients" },
    { label: client?.name || "Client", link: `/clients/${clientId}` },
    { label: "Add Block", link: "" },
  ];

  return (
    <div className="container">
      <Breadcrumbs items={breadcrumbs} />

      <div className="row">
        <div className="col-md-12">
          <div className="panel panel-default panel-form">
            <div className="panel-heading panel-title">
              <h3>Add Block</h3>
            </div>
            <div className="panel-body">
              <form onSubmit={handleSubmit}>
                <fieldset className="form-horizontal">
                  {/* Client (read-only) */}
                  <div className="form-group">
                    <label className="control-label col-sm-3">Client</label>
                    <div className="col-sm-5">
                      <div className="as-input">{client?.name}</div>
                    </div>
                  </div>

                  {/* Block Name */}
                  <div className={`form-group ${errors.name ? "has-error" : ""}`}>
                    <label className="control-label col-sm-3 label-required">
                      Name
                    </label>
                    <div className="col-sm-5">
                      <input
                        type="text"
                        className="form-control"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        placeholder="Enter block name"
                      />
                      {errors.name && (
                        <span className="help-block text-danger">{errors.name}</span>
                      )}
                    </div>
                  </div>
                </fieldset>

                <fieldset className="form-horizontal mt-30">
                  <div className="col-sm-3"></div>
                  <div className="col-sm-5 text-right">
                    <Link to={`/clients/${clientId}`} className="btn btn-default" style={{ marginRight: "10px" }}>
                      Cancel
                    </Link>
                    <button
                      type="submit"
                      className="btn btn-head btn-standard"
                      disabled={submitting}
                    >
                      <i className="glyphicon glyphicon-plus"></i>{" "}
                      {submitting ? "Adding..." : "Add"}
                    </button>
                  </div>
                </fieldset>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlockCreate;
